import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchApi } from '../../utilities/api';
import { ENDPOINTS } from '../../utilities/endpoints';

export interface User {
  userId:      string;
  email:       string;
  accessToken: string;
  webUrl:      string;
  status:      string;
  deviceToken?: string;
}

export interface UserProfile {
  userId?:             string;    
  userType:           string;
  firstName:          string;
  lastName:           string;
  email:              string;
  profilePic:         string;
  facebookId:         string;
  googleId:           string;
  insertDate:         number;
  userName:           string;
  webUrl:             string;
  fullName:           string;
  isCardAdded:        boolean;
  buildingColor:      string;
  totalPropertyPrice: number;
  numberOfProperties: number;
  stripeConnAccId:    string ;
    isStripeIntegrated?: boolean;
}

interface LoginCredentials {
  email:       string;
  password:    string;
  deviceToken?: string;
}

interface ResetPasswordCredentials {
  email: string;
  otpToken: number;
  newPassword: string;
}

interface AuthState {
  user:             User | null;
  isAuthenticated:  boolean;
  isLoading:        boolean;
  error:            string | null;

  profile:          UserProfile | null;
  isFetchingProfile: boolean;
  profileError:      string | null;

  isUpdatingProfile: boolean;
  updateError:       string | null;
}

const initialState: AuthState = {
  user:              null,
  isAuthenticated:   false,
  isLoading:         false,
  error:             null,
  profile:           null,
  isFetchingProfile: false,
  profileError:      null,
  isUpdatingProfile: false,
  updateError:       null,
};


export const loginUser = createAsyncThunk<
  User,
  LoginCredentials,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await fetchApi<User>(ENDPOINTS.USERS.LOGIN, {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
      if (res.data.accessToken) {
        localStorage.setItem('token', res.data.accessToken);
      }
      if (res.data.userId) {
        localStorage.setItem('userId', res.data.userId);
      }
      return res.data;
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.message || 'Login failed';
      return rejectWithValue(msg);
    }
  }
);

export const createOtp = createAsyncThunk<
  any,
  { email: string; type: string },
  { rejectValue: string }
>(
  'auth/createOtp',
  async ({ email, type }, { rejectWithValue }) => {
    try {
      const res = await fetchApi<any>(ENDPOINTS.OTP.CREATE, {
        method: 'POST',
        body: JSON.stringify({ email, type }),
      });
      return res.data;
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.message || 'OTP creation failed';
      return rejectWithValue(msg);
    }
  }
);

export const verifyOtp = createAsyncThunk<
  any,
  { email: string; type: string; otp: number },
  { rejectValue: string }
>(
  'auth/verifyOtp',
  async ({ email, type, otp }, { rejectWithValue }) => {
    try {
      const res = await fetchApi<any>(ENDPOINTS.OTP.VERIFY, {
        method: 'POST',
        body: JSON.stringify({ email, type, otp }),
      });
      return res.data;
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.message || 'OTP verification failed';
      return rejectWithValue(msg);
    }
  }
);

export const registerUser = createAsyncThunk<
  User,
  {
    email: string;
    password: string;
    otpToken: number;
    fullName: string;
    userName: string;
    deviceToken?: string;
  },
  { rejectValue: string }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const body = {
        email:      userData.email,
        password:   userData.password,
        otpToken:   userData.otpToken,
        fullName:   userData.fullName,
        userName:   userData.userName,
        userType:   'app',
        appType:    'ios',
        deviceToken:userData.deviceToken,
      };
      const res = await fetchApi<User>(ENDPOINTS.USERS.REGISTER, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.data.accessToken) {
        localStorage.setItem('token', res.data.accessToken);
      }
      if (res.data.userId) {
        localStorage.setItem('userId', res.data.userId);
      }
      return res.data;
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.message || 'Registration failed';
      return rejectWithValue(msg);
    }
  }
);

export const fetchUserProfile = createAsyncThunk<
  UserProfile,
  void,
  { rejectValue: string }
>(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('fetchUserProfile → no token found');
      return rejectWithValue('No auth token found');
    }

    try {
      const res = await fetchApi<UserProfile>(
        ENDPOINTS.USERS.PROFILE,
        {
          method: 'GET',
          headers: { Authorization: token },
        }
      );

      return res.data;
    } catch (err: any) {
      console.error('fetchUserProfile → error fetching profile:', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch profile';
      return rejectWithValue(msg);
    }
  }
);

export const resetPassword = createAsyncThunk<
  any,
  ResetPasswordCredentials,
  { rejectValue: string }
>(
  'auth/resetPassword',
  async ({ email, otpToken, newPassword }, { rejectWithValue }) => {
    try {
      const res = await fetchApi<any>(ENDPOINTS.USERS.FORGOT_PASSWORD, {
        method: 'POST',
        body: JSON.stringify({ email, otpToken, newPassword }),
      });
      return res.data;
    } catch (err: any) {
      const msg = err.response?.data?.data?.message || err.message || 'Password reset failed';
      return rejectWithValue(msg);
    }
  }
);

export const updateUserProfile = createAsyncThunk<
  UserProfile,
  {
    buildingColor: string;
    userName:      string;
    email:         string;
    fullName:      string;
  },
  { rejectValue: string }
>(
  'auth/updateProfile',
  async (payload, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('updateUserProfile → no auth token');
      return rejectWithValue('No auth token found');
    }

    try {
      const res = await fetchApi<{ data: UserProfile }>(
        ENDPOINTS.USERS.UPDATE,
        {
          method: 'PUT',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      // <-- log the raw API response

      // <-- log the deserialized user profile

      return res.data.data;
    } catch (err: any) {
      console.error('updateUserProfile → error:', err);
      const msg =
        err.response?.data?.message || err.message || 'Failed to update profile';
      return rejectWithValue(msg);
    }
  }
);

export const deleteUser = createAsyncThunk<
  string, 
  string, 
  { rejectValue: string }
>(
  'auth/deleteUser',
  async (userId, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No auth token found');
    }
    try {
      const res = await fetchApi<{ message: string }>(
        ENDPOINTS.USERS.DELETE(userId),
        {
          method: 'DELETE',
          headers: {
            Authorization: token,
            accept:        'application/json',
          },
        }
      );
      return res.data.message;
    } catch (err: any) {
      const msg =
        err.response?.data?.data?.message ||
        err.message ||
        'Failed to delete user';
      return rejectWithValue(msg);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user             = null;
      state.isAuthenticated  = false;
      state.error            = null;
      state.profile          = null;
      state.profileError     = null;
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
    },
    clearError: (state) => {
      state.error        = null;
      state.profileError = null;
      state.updateError  = null;

    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (s) => { s.isLoading = true;  s.error = null; })
      .addCase(loginUser.fulfilled, (s, a: PayloadAction<User>) => {
        s.isLoading       = false;
        s.user            = a.payload;
        s.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (s, a) => {
        s.isLoading       = false;
        s.error           = a.payload ?? a.error.message ?? 'Login failed';
        s.isAuthenticated = false;
      });

    builder
      .addCase(createOtp.pending,   (s) => { s.isLoading = true;  s.error = null; })
      .addCase(createOtp.fulfilled, (s) => { s.isLoading = false; })
      .addCase(createOtp.rejected,  (s, a) => {
        s.isLoading = false;
        s.error     = a.payload ?? a.error.message ?? 'OTP creation failed';
      });

    builder
      .addCase(verifyOtp.pending,   (s) => { s.isLoading = true;  s.error = null; })
      .addCase(verifyOtp.fulfilled, (s) => { s.isLoading = false; })
      .addCase(verifyOtp.rejected,  (s, a) => {
        s.isLoading = false;
        s.error     = a.payload ?? a.error.message ?? 'OTP verification failed';
      });

    builder
      .addCase(registerUser.pending,   (s) => { s.isLoading = true;  s.error = null; })
      .addCase(registerUser.fulfilled, (s, a: PayloadAction<User>) => {
        s.isLoading       = false;
        s.user            = a.payload;
        s.isAuthenticated = true;
      })
      .addCase(registerUser.rejected,  (s, a) => {
        s.isLoading       = false;
        s.error           = a.payload ?? a.error.message ?? 'Registration failed';
      });

    builder
      .addCase(fetchUserProfile.pending,   (s) => { s.isFetchingProfile = true;  s.profileError = null; })
      .addCase(fetchUserProfile.fulfilled, (s, a: PayloadAction<UserProfile>) => {
        s.isFetchingProfile = false;
        s.profile           = a.payload;
      })
      .addCase(fetchUserProfile.rejected,  (s, a) => {
        s.isFetchingProfile = false;
        s.profileError      = a.payload ?? a.error.message ?? 'Failed to fetch profile';
      });

      builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isUpdatingProfile = true;
        state.updateError       = null;
      })
      .addCase(
        updateUserProfile.fulfilled,
        (state, action: PayloadAction<UserProfile>) => {
          state.isUpdatingProfile = false;
          state.profile           = action.payload;
        }
      )
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isUpdatingProfile = false;
        state.updateError       = action.payload ?? action.error.message ?? 'Update failed';
      });

      builder
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error     = null;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading       = false;
        state.user            = null;
        state.isAuthenticated = false;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error     = action.payload ?? action.error.message ?? 'Delete failed';
      });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Password reset failed';
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
